import { Task, type ProcessMessage, type TaskDefinition } from '@st/redux'
import {
  type HttpStatusError,
  type SDKCommand,
  type SDKRequestFor,
  type SDKResponseFor
} from '@st/sdk'
import { Effect, Stream } from 'effect'
import { isMatching, match, P } from '@st/pattern'
import {
  observeAuthStatus,
  observeOrganizationContextStates,
  observeLocations,
  type Auth,
  type Routing,
  type SdkFailure
} from '~/effects'
import type { OrganizationContextState } from '~/effects'
import type { AuthStatusEvent } from '~/features/auth/auth-client-adapter'
import type { RouteLocation } from '~/features/routing/location-change'
import { routes, type AppRoute } from '~/features/routes'

export type RouteChanged<Route extends AppRoute = AppRoute> = {
  type: 'routeChanged'
  route: Route | null
  location: RouteLocation
}

type RouteNarrowedBy<Pattern> = P.narrow<AppRoute, Pattern> & AppRoute

/**
 * Defines one module-scoped Task from the platform location stream. Mapping is
 * definition-time feature policy; modules never acquire callbacks or manage
 * routing resources themselves.
 */
export function observeLocation<Message extends ProcessMessage>(
  mapLocation: (location: RouteLocation) => Message | null
): TaskDefinition<readonly [], Message, Routing> {
  return Task.define(() => Task.pipe(observeLocations, Task.map(mapLocation)))
}

/** Defines one module-scoped Task from the canonical Auth status stream. */
export function observeAuth<Message extends ProcessMessage>(
  mapStatus: (status: AuthStatusEvent) => Message | null
): TaskDefinition<readonly [], Message, Auth> {
  return Task.define(() => Task.pipe(observeAuthStatus, Task.map(mapStatus)))
}

/** Defines a module-scoped Task from the canonical organization context. */
export function observeOrganizationContext<Message extends ProcessMessage>(
  mapContext: (context: OrganizationContextState) => Message | null
) {
  return Task.define(() => Task.pipe(observeOrganizationContextStates, Task.map(mapContext)))
}

/**
 * Defines a route observation from the same structural patterns used by
 * `@st/pattern`. Matching routes retain their narrowed type; every other
 * location emits `null` so leaving the feature's route remains observable.
 *
 * Define the observer once at module scope, then return its declaration from
 * `tasks(state)`. The pattern is definition-time feature policy, never Task
 * identity or serializable application data.
 */
export function observeRoute<const Pattern extends P.Pattern<AppRoute>>(
  pattern: P.valid<AppRoute, Pattern>
): TaskDefinition<readonly [], RouteChanged<RouteNarrowedBy<Pattern>>, Routing> {
  const matchesRoute = isMatching(pattern)

  return observeLocation((location): RouteChanged<RouteNarrowedBy<Pattern>> => {
    const route = routes.match(location.pathname) ?? null

    if (route != null && matchesRoute(route)) {
      return {
        type: 'routeChanged',
        route: route as RouteNarrowedBy<Pattern>,
        location
      }
    }
    return {
      type: 'routeChanged',
      route: null,
      location
    }
  })
}

export type OrganizationRouteContext<Route extends AppRoute> =
  | { status: 'inactive'; route: null }
  | {
      status: 'loading'
      route: Route
      currentUserId: string | null
    }
  | {
      status: 'loadFailed'
      route: Route
      currentUserId: string | null
    }
  | {
      status: 'unavailable'
      route: Route
      currentUserId: string | null
    }
  | {
      status: 'loaded'
      route: Route
      currentUserId: string | null
      organization: Extract<OrganizationContextState, { status: 'loaded' }>['organization']
    }

/**
 * Joins a narrowed route with the canonical organization context.
 * Mismatched route/context snapshots are exposed as loading, never as a stale
 * organization. Callers receive one coherent input and own no synchronization.
 */
type OrganizationAppRoute = Extract<AppRoute, { organizationSlug: string }>
type OrganizationRouteNarrowedBy<Pattern> = P.narrow<
  OrganizationAppRoute,
  Pattern
> &
  OrganizationAppRoute

export function observeOrganizationRoute<const Pattern extends P.Pattern<OrganizationAppRoute>>(
  pattern: P.valid<OrganizationAppRoute, Pattern>
) {
  const matchesRoute = isMatching(pattern)

  return Task.define(() =>
    Task.pipe(
      Stream.zipLatest(observeLocations, observeOrganizationContextStates),
      Task.map(([location, organizationContext]) => {
        const route = routes.match(location.pathname) ?? null
        if (route == null || !('organizationSlug' in route) || !matchesRoute(route)) {
          return {
            type: 'organizationRouteChanged',
            context: { status: 'inactive', route: null }
          } as const
        }

        const narrowedRoute = route as OrganizationRouteNarrowedBy<Pattern>
        if (organizationContext.organizationSlug != narrowedRoute.organizationSlug) {
          return {
            type: 'organizationRouteChanged',
            context: {
              status: 'loading',
              route: narrowedRoute,
              currentUserId: organizationContext.currentUserId
            }
          } as const
        }

        return {
          type: 'organizationRouteChanged',
          context: match(organizationContext)
            .returnType<OrganizationRouteContext<OrganizationRouteNarrowedBy<Pattern>>>()
            .with({ status: 'loading' }, (context) => ({
              status: 'loading',
              route: narrowedRoute,
              currentUserId: context.currentUserId
            }))
            .with({ status: 'loadFailed' }, (context) => ({
              status: 'loadFailed',
              route: narrowedRoute,
              currentUserId: context.currentUserId
            }))
            .with({ status: 'unavailable' }, (context) => ({
              status: 'unavailable',
              route: narrowedRoute,
              currentUserId: context.currentUserId
            }))
            .with({ status: 'loaded' }, (context) => ({
              status: 'loaded',
              route: narrowedRoute,
              currentUserId: context.currentUserId,
              organization: context.organization
            }))
            .exhaustive()
        } as const
      })
    )
  )
}

/**
 * Observes the authenticated user at the app boundary. Feature modules receive
 * only the serializable identity they need; a login attempt failing does not
 * imply that the current identity changed.
 */
export const observeCurrentUser = observeAuth((event): CurrentUserChanged | null =>
  match(event)
    .returnType<CurrentUserChanged | null>()
    .with({ type: 'loggedIn' }, ({ user }) => ({
      type: 'currentUserChanged',
      currentUserId: user.id
    }))
    .with({ type: 'loggedOut' }, () => ({
      type: 'currentUserChanged',
      currentUserId: null
    }))
    .with({ type: 'loginFailed' }, () => null)
    .exhaustive()
)

type CurrentUserChanged = {
  type: 'currentUserChanged'
  currentUserId: string | null
}

export type SdkRequestResult<Response> =
  | { ok: true; response: Response }
  | { ok: false; error: SdkFailure }

type RunSdkRequestOptions<Env> = {
  onError?: (error: SdkFailure) => Effect.Effect<void, never, Env>
}

export function runSdkRequest<Response, Env, ErrorEnv = never>(
  effect: Effect.Effect<Response, SdkFailure, Env>,
  options?: RunSdkRequestOptions<ErrorEnv>
): Effect.Effect<SdkRequestResult<Response>, never, Env | ErrorEnv> {
  const failed = (error: SdkFailure) => {
    const onError = options?.onError
    if (!onError) {
      return Effect.succeed({ ok: false as const, error })
    }

    return onError(error).pipe(Effect.as({ ok: false as const, error }))
  }

  return effect.pipe(
    Effect.matchEffect({
      onSuccess: (response) => Effect.succeed({ ok: true as const, response }),
      onFailure: failed
    })
  )
}

export type CallSdkRequestResult<Response> =
  | { type: 'ok'; response: Response }
  | { type: 'unauthorized'; error: HttpStatusError }
  | { type: 'failed'; error: SdkFailure }

/**
 * Sends one request and names its outcome. A 401 is unauthorized; any other
 * failure, network or server, is a failed request. The SDK client has already
 * reported server errors.
 */
export function callSdkRequest<RequestType extends SDKCommand['type'], Env, ErrorEnv = never>(
  send: (
    request: SDKRequestFor<RequestType>
  ) => Effect.Effect<SDKResponseFor<RequestType>, SdkFailure, Env>,
  request: SDKRequestFor<RequestType>,
  options?: RunSdkRequestOptions<ErrorEnv>
): Effect.Effect<CallSdkRequestResult<SDKResponseFor<RequestType>>, never, Env | ErrorEnv> {
  return runSdkRequest(send(request), options).pipe(
    Effect.map((result): CallSdkRequestResult<SDKResponseFor<RequestType>> => {
      if (result.ok) return { type: 'ok', response: result.response }
      if (result.error._tag == 'HttpStatusError' && result.error.response.status == 401) {
        return { type: 'unauthorized', error: result.error }
      }
      return { type: 'failed', error: result.error }
    })
  )
}
